/**
 * Polish content for the two Letters healing arcs.
 *
 * Source: Marta Stankiewicz, Drive doc "05 — The Letters Arcs", returned
 * 2026-07-27. These arcs are her ritual and her methodology, and her brief
 * asked her to write the Polish as if composing it fresh rather than
 * translating. That is what she did, so this is authored copy, not a rendering
 * of the English.
 *
 * VOICE CHANGE, DELIBERATE
 * The English attributionBio is third person ("Marta Stankiewicz is a
 * trauma-informed facilitator…"). Her Polish is first person ("Jestem
 * facylitatorką…"). She was explicitly asked to write her own bio rather than
 * translate ours, and she chose to address the reader directly. Preserved as
 * written — do not "fix" it back to third person to match the English.
 *
 * GENDERED FORMS
 * Unlike the UI pack and the guided arcs, doc 05 came back already free of dual
 * "gotowy/gotowa" constructions throughout, so nothing here needed
 * neutralising. Every string below is Marta's.
 */
import type { Arc, ArcId } from './arcs'

/** Polish overrides for one arc. Structural fields (colors, pacing hours,
 *  burnRequired, warningIf) are never localized — only what a user reads. */
export interface ArcPl {
  name: string
  tagline: string
  description: string
  attribution: string
  attributionBio: string
  pacingSoftLabel: string
  /** Parallel to preScreenQuestions — same order, same length. */
  preScreen: { text: string; warningMessage: string }[]
  /** Parallel to sessions — same order, same length. */
  sessions: { title: string; subtitle: string; description: string }[]
  completionLine: string
}

export const ARCS_PL: Record<ArcId, ArcPl> = {
  letters_to_others: {
    name: 'Listy do innych',
    tagline: 'Wypowiedz słowa, które nigdy nie padły.',
    description:
      'Trzyetapowy rytuał, który pozwoli Ci odłożyć ciężar noszony po zranieniu przez drugą osobę. '
      + 'Nie po to, by ją usprawiedliwić — lecz po to, by odzyskać siebie.',
    attribution: 'Rytuał autorski Marty Stankiewicz · Awaken By Love',
    attributionBio:
      'Jestem facylitatorką pracy z traumą i założycielką Awaken By Love. '
      + 'Poprzez somatyczną praktykę wybaczenia towarzyszę ludziom w drodze do ponownego '
      + 'odnajdywania i odzyskiwania siebie. Te listy stanowią adaptację mojego autorskiego '
      + 'Rytuału Listu Wybaczenia.',
    pacingSoftLabel: 'Daj sobie przynajmniej 3 dni. Jeśli czujesz taką potrzebę — nawet cały tydzień.',
    preScreen: [
      {
        text: 'Czy krzywda, którą w sobie nosisz, jest wciąż świeża i nieprzebolana — czy to coś, na czego pełne doświadczenie i odczucie nie było jeszcze przestrzeni?',
        warningMessage:
          'Ten cykl przynosi najlepsze rezultaty, gdy od pierwotnej rany dzieli Cię już pewien dystans. '
          + 'Zanim zaczniesz tutaj, dobrym pierwszym krokiem może być zwykła sesja poświęcona temu, co w sobie nosisz.',
      },
      {
        text: 'Czy ta osoba wciąż obecna jest w Twoim życiu w sposób, który daje Ci poczucie braku bezpieczeństwa lub braku domknięcia?',
        warningMessage:
          'Ten cykl służy wewnętrznemu uwolnieniu — nie chodzi w nim o pojednanie, kontakt czy '
          + 'podejmowanie jakichkolwiek działań wobec tej osoby. Jeśli istnieje realne zagrożenie lub '
          + 'Twoje poczucie bezpieczeństwa jest zachwiane, przed rozpoczęciem skonsultuj się z terapeutą.',
      },
    ],
    sessions: [
      {
        title: 'Niewypowiedziana prawda',
        subtitle: 'Sesja 1 z 3',
        description: 'To, co nigdy nie zostało powiedziane. Słowa przelane tutaj. Spalone, zanim odejdziesz.',
      },
      {
        title: 'Głęboka warstwa',
        subtitle: 'Sesja 2 z 3',
        description: 'To, co stracone. To, za czym była tęsknota. Żal i smutek skryte pod złością.',
      },
      {
        title: 'Uwolnienie',
        subtitle: 'Sesja 3 z 3',
        description: 'Nie po to, by ich usprawiedliwić. Po to, by odzyskać siebie.',
      },
    ],
    completionLine: 'Udało Ci się odłożyć tu pewien ciężar. To ma ogromne znaczenie.',
  },

  letters_to_self: {
    name: 'Listy do Siebie',
    tagline: 'Powróć do tej części siebie, która została w tyle.',
    description:
      'Trzyetapowa podróż przez doświadczenie opuszczenia siebie i powrót do własnego wnętrza. '
      + 'Dla tej części Ciebie, która czekała, by zostać odnalezioną.',
    attribution: 'Rytuał autorski Marty Stankiewicz · Awaken By Love',
    attributionBio:
      'Jestem facylitatorką pracy z traumą i założycielką Awaken By Love. '
      + 'Poprzez somatyczną praktykę wybaczenia towarzyszę ludziom w drodze do ponownego '
      + 'odnajdywania i odzyskiwania siebie. Ten cykl stanowi adaptację mojego autorskiego '
      + 'Rytuału Powrotu do Siebie.',
    pacingSoftLabel: 'Daj sobie przynajmniej 3 dni przerwy między sesjami.',
    preScreen: [
      {
        text: 'Czy potrafisz dzisiaj spotkać się ze sobą z choćby odrobiną łagodności — nawet jeśli będzie to nieidealne?',
        warningMessage:
          'Ten cykl wymaga przyjęcia siebie z troską i uważnością. Jeśli czujesz, że na ten moment '
          + 'jest to niemożliwe, bardziej wspierająca może okazać się zwykła sesja poświęcona temu, '
          + 'co obecnie w sobie nosisz.',
      },
    ],
    sessions: [
      {
        title: 'Głos bólu',
        subtitle: 'Sesja 1 z 3',
        description: 'Pozwól przemówić tej części, która została porzucona. Bez cenzury. Bez łagodzenia krawędzi.',
      },
      {
        title: 'Głos zrozumienia',
        subtitle: 'Sesja 2 z 3',
        description: 'Odpowiedz sobie — nie po to, by cokolwiek naprawiać, lecz by zacząć rozumieć.',
      },
      {
        title: 'Obietnica',
        subtitle: 'Sesja 3 z 3',
        description: 'Obietnica, że zostaniesz. Że już nigdy nie opuścisz siebie.',
      },
    ],
    completionLine: 'To nie jest koniec. To dopiero początek.',
  },
}

/**
 * Returns the arc with its user-visible copy swapped for the requested
 * language. Structural fields are passed through untouched — notably
 * `warningIf`, `burnRequired`, `alwaysSaved` and `pacingHours`, which drive the
 * pre-screen gate and the burn ritual. Translating those would change behaviour,
 * not just wording.
 *
 * Pre-screen questions and sessions are merged positionally, and only where the
 * Polish array is long enough, so a short or missing translation leaves the
 * corresponding English entry intact rather than dropping a safety gate.
 */
export function localizeArc(arc: Arc, lang?: string): Arc {
  if (lang !== 'pl') return arc

  const pl = ARCS_PL[arc.id]
  if (!pl) return arc

  return {
    ...arc,
    name: pl.name,
    tagline: pl.tagline,
    description: pl.description,
    attribution: pl.attribution,
    attributionBio: pl.attributionBio,
    pacingSoftLabel: pl.pacingSoftLabel,
    preScreenQuestions: arc.preScreenQuestions.map((q, i) => {
      const t = pl.preScreen[i]
      return t ? { ...q, text: t.text, warningMessage: t.warningMessage } : q
    }),
    sessions: arc.sessions.map((s, i) => {
      const t = pl.sessions[i]
      return t ? { ...s, title: t.title, subtitle: t.subtitle, description: t.description } : s
    }),
    completionLine: pl.completionLine,
  }
}
